import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useSalon } from '../../contexts/SalonContext';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import AppointmentTimeline from '../booking/timeline/AppointmentTimeline';
import { format } from 'date-fns';

// Importar as funções centralizadas de appointments
import { getAppointments } from '../../firebase';

// Modal
import AppointmentDetailModal from '../admin/AppointmentDetailModal';

/**
 * Página de agenda do profissional
 */
const ProfessionalAgendaPage = () => {
  const { selectedSalonId } = useUser(); // Obter salonId do contexto do usuário
  const { currentUser } = useAuth();
  const { salonConfig } = useSalon();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [professionalId, setProfessionalId] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [professionals, setProfessionals] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Carregar dados do profissional e agendamentos
  useEffect(() => {
    const loadData = async () => {
      if (!selectedSalonId || !currentUser) {
        return;
      }
      
      setLoading(true);
      try {
        // 1. Buscar ID do profissional
        const profQuery = query(
          collection(db, 'salons', selectedSalonId, 'professionals'),
          where('userId', '==', currentUser.uid)
        );
        
        const profSnapshot = await getDocs(profQuery);
        if (profSnapshot.empty) {
          setError('Profissional não encontrado');
          setLoading(false);
          return;
        }
        
        const profDoc = profSnapshot.docs[0];
        const profId = profDoc.id;
        const profData = profDoc.data();
        
        setProfessionalId(profId);
        setProfessional({
          id: profId,
          ...profData,
          nome: profData.name || 'Profissional'
        });
        
        setProfessionals([{
          id: profId,
          ...profData,
          nome: profData.name || 'Profissional'
        }]);
        
        // 2. Buscar agendamentos usando a função centralizada
        try {
          // Configurar filtro para buscar agendamentos do dia selecionado
          // Usar o formato de string para data, que é como está armazenado na maioria dos agendamentos
          const filters = { 
            professionalId: profId,
            // Usar filtro de string para data (YYYY-MM-DD)
            stringDateFormat: true,
            data: selectedDate
          };
          
          console.log(`[ProfessionalAgendaPage] Buscando agendamentos para o dia ${selectedDate}`, filters);
          const appointmentsData = await getAppointments(selectedSalonId, filters);
          console.log(`[ProfessionalAgendaPage] Agendamentos encontrados:`, appointmentsData.length, appointmentsData);
          
          // Processar cada agendamento para o formato esperado pelo AppointmentTimeline
          const processedAppointments = appointmentsData.map(appointment => {
            console.log("Processando agendamento:", appointment.id, appointment);
            
            // Criar objeto Date para horário de início
            let startTime;
            if (appointment.dataTimestamp && typeof appointment.dataTimestamp.toDate === 'function') {
              startTime = appointment.dataTimestamp.toDate();
            } else if (appointment.startTime && typeof appointment.startTime.toDate === 'function') {
              startTime = appointment.startTime.toDate();
            } else if (appointment.data && appointment.horario) {
              const [year, month, day] = appointment.data.split('-').map(Number);
              const [hours, minutes] = appointment.horario.split(':').map(Number);
              startTime = new Date(year, month - 1, day, hours, minutes);
            } else if (appointment.startTime instanceof Date) {
              startTime = appointment.startTime;
            } else {
              // Fallback para criar uma data a partir da string de data e horário
              const [year, month, day] = selectedDate.split('-').map(Number);
              startTime = new Date(year, month - 1, day, 0, 0); // Horário padrão 00:00
            }
            
            // Criar objeto Date para horário de término
            let endTime;
            if (appointment.endTime && typeof appointment.endTime.toDate === 'function') {
              endTime = appointment.endTime.toDate();
            } else if (appointment.data && appointment.horarioFim) {
              const [year, month, day] = appointment.data.split('-').map(Number);
              const [hours, minutes] = appointment.horarioFim.split(':').map(Number);
              endTime = new Date(year, month - 1, day, hours, minutes);
            } else if (appointment.duracaoTotal && startTime) {
              endTime = new Date(startTime.getTime() + (appointment.duracaoTotal * 60 * 1000));
            } else if (appointment.endTime instanceof Date) {
              endTime = appointment.endTime;
            } else {
              // Fallback para criar uma data 1 hora após o início
              endTime = new Date(startTime.getTime() + (60 * 60 * 1000));
            }
            
            // Mapear o objeto para o formato esperado pelo AppointmentTimeline
            return {
              id: appointment.id,
              title: appointment.cliente || appointment.clientName || 'Cliente',
              start: startTime,
              end: endTime,
              resourceId: profId,
              // Campos para compatibilidade com o Timeline
              profissional: profId,
              cliente: appointment.cliente || appointment.clientName || 'Cliente',
              data: selectedDate,
              horario: format(startTime, 'HH:mm'),
              horarioFim: format(endTime, 'HH:mm'),
              duracaoTotal: Math.floor((endTime - startTime) / (1000 * 60)),
              servicoNome: Array.isArray(appointment.servicos) && appointment.servicos.length > 0 
                ? appointment.servicos[0].nome 
                : Array.isArray(appointment.services) && appointment.services.length > 0 
                ? appointment.services[0].name 
                : appointment.serviceName || 'Serviço',
              preco: appointment.valorTotal || appointment.price || 0,
              cancelado: appointment.cancelado || appointment.status === 'cancelled',
              confirmado: appointment.confirmado || appointment.status === 'confirmed' || appointment.status === 'completed',
              // Manter objeto original para acesso completo aos dados
              appointment: appointment
            };
          });
          
          console.log("[ProfessionalAgendaPage] Agendamentos processados:", processedAppointments);
          setAppointments(processedAppointments);
        } catch (appErr) {
          console.error("[ProfessionalAgendaPage] Erro ao carregar agendamentos:", appErr);
          setError('Erro ao carregar agendamentos: ' + appErr.message);
        }
        
      } catch (err) {
        console.error('[ProfessionalAgendaPage] Erro ao carregar agenda:', err);
        setError('Erro ao carregar agenda: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedSalonId, currentUser, selectedDate]);
  
  // Manipulador para atualizar um agendamento
  const handleAppointmentUpdate = (updatedAppointment) => {
    // Atualizar localmente a lista de agendamentos
    setAppointments(prevAppointments => 
      prevAppointments.map(apt => 
        apt.id === updatedAppointment.id ? updatedAppointment : apt
      )
    );
  };
  
  // Manipulador para bloquear horário
  const handleTimeBlocked = (blockedTime) => {
    // Adicionar o bloqueio à lista de agendamentos
    setAppointments(prevAppointments => [...prevAppointments, blockedTime]);
  };
  
  // Manipulador para mudança de visualização
  const handleViewChange = (view) => {
    console.log('Mudança de visualização:', view);
  };
  
  // Manipulador para seleção de evento
  const handleSelectEvent = (event) => {
    if (event && event.appointment) {
      setSelectedAppointment(event.appointment);
      setIsModalOpen(true);
    }
  };
  
  // Renderizar estado de carregamento
  if (loading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }
  
  // Renderizar erro
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Minha Agenda</h1>
      
      {/* Conteúdo da agenda */}
      <div className="bg-white rounded-lg shadow">
        {appointments.length === 0 && !loading ? (
          <div className="text-center py-10">
            <p className="text-gray-500">Nenhum agendamento encontrado para esta data</p>
          </div>
        ) : (
          <AppointmentTimeline
            appointments={appointments}
            selectedDate={selectedDate}
            professionals={professionals}
            salonConfig={salonConfig}
            onEventUpdate={handleAppointmentUpdate}
            onTimeBlocked={handleTimeBlocked}
            onViewChange={handleViewChange}
            onSelectEvent={handleSelectEvent}
          />
        )}
      </div>
      
      {/* Modal de detalhes do agendamento */}
      {isModalOpen && selectedAppointment && (
        <AppointmentDetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          appointment={selectedAppointment}
          onUpdate={handleAppointmentUpdate}
          professionalView={true}
          salonId={selectedSalonId}
        />
      )}
    </div>
  );
};

export default ProfessionalAgendaPage; 